import {Alert, Box, CircularProgress, Stack, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useRef, useState} from 'react';
import {useProfile} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {getCurrentStay} from "crqlar-react-common/dist/models/profile/profile.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {CasablancaCheckinPreviewResponse} from "crqlar-react-common/dist/models/billing/folio.model";
import {FOLIO_ALERT_SX, FOLIO_FIELD_GAP} from "../editor/folioStyles";

interface CasablancaProviderProps {
    onDataChange: (isValid: boolean, data: any) => void;
    profileId?: string;
    /** The folio dialog's payment block: a small field and alerts, no title repeating the chosen payment method. */
    compact?: boolean;
}

const CasablancaProvider = ({profileId, onDataChange, compact}: CasablancaProviderProps) => {
    const {t} = useTranslation();
    const alertSx = compact ? FOLIO_ALERT_SX : undefined;
    const profile = useProfile(profileId);
    const [roomId, setRoomId] = useState('');
    // Result of the last in-house lookup; staff confirm the guest here before the Charge button enables.
    const [preview, setPreview] = useState<CasablancaCheckinPreviewResponse | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);

    // Auto-fill the room from the guest's current stay (once).
    useEffect(() => {
        if (profile?.id && !roomId) {
            setRoomId(getCurrentStay(profile)?.roomId || '');
        }
    }, [profile?.id]);

    const room = roomId.trim();
    // Tracks the room of the most recent request so we can ignore stale in-flight responses
    // (we compare against the room WE asked about, not the room the server echoes back).
    const latestRoomRef = useRef('');
    // The charge is only valid once we've confirmed an in-house guest for the CURRENT room —
    // this is what stops a charge from silently landing on whoever happens to occupy the room.
    // `preview` always corresponds to the current room (cleared on change, stale responses ignored).
    const verifiedGuest = !!preview?.found;

    // Verify the room (debounced) whenever it changes. Each change clears the previous result so
    // the Charge button stays disabled until the new room is re-confirmed.
    useEffect(() => {
        setPreview(null);
        latestRoomRef.current = room;
        if (!room) {
            setIsVerifying(false);
            return;
        }
        setIsVerifying(true);
        const handle = setTimeout(async () => {
            try {
                const result = await FolioService.previewCasablancaCheckin({roomNumber: room});
                if (latestRoomRef.current === room) setPreview(result);
            } catch (e) {
                console.error('Casablanca check-in preview failed:', e);
                if (latestRoomRef.current === room) setPreview({found: false, roomNumber: room, error: 'casablanca.pos.unreachable'});
            } finally {
                if (latestRoomRef.current === room) setIsVerifying(false);
            }
        }, 600);
        return () => clearTimeout(handle);
    }, [room]);

    useEffect(() => {
        onDataChange(verifiedGuest, {roomId: room, provider: 'casablanca'});
    }, [verifiedGuest, room]);

    return (
        <Box sx={{gap: compact ? FOLIO_FIELD_GAP : '12px', width: '100%', display: 'flex', flexDirection: 'column'}}>
            {!compact && (
                <Typography>
                    {t('casablanca.provider.title')}
                </Typography>
            )}

            <TextField
                fullWidth
                size={compact ? 'small' : undefined}
                label={t('casablanca.provider.roomNumber')}
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                required
                helperText={t('casablanca.provider.roomNumber.helper')}
            />

            {isVerifying && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16}/>
                    <Typography variant="body2">{t('casablanca.provider.checking')}</Typography>
                </Stack>
            )}

            {!isVerifying && verifiedGuest && (
                <Alert severity="success" sx={alertSx}>
                    {t('casablanca.provider.guestConfirm', {
                        room: preview?.roomNumber,
                        guest: preview?.guestName || t('casablanca.provider.unnamedGuest')
                    })}
                    {(preview?.arrival || preview?.departure) && (
                        <Typography variant="caption" component="div" sx={{mt: 0.5, opacity: 0.8}}>
                            {preview?.arrival} – {preview?.departure}
                        </Typography>
                    )}
                </Alert>
            )}

            {!isVerifying && preview && !preview.found && (
                <Alert severity="error" sx={alertSx}>
                    {t(preview.error || 'Charging failed')}
                    {preview.message && (
                        <Typography variant="caption" component="div" sx={{mt: 0.5, opacity: 0.8}}>
                            {preview.message}
                        </Typography>
                    )}
                </Alert>
            )}
        </Box>
    );
};

export default CasablancaProvider;
